import {
  createElement,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  forwardRef,
  useCallback,
  FocusEvent,
  MouseEvent,
  useImperativeHandle,
} from 'react';
import { BaseInputProps } from './types';
import { CustomMessage, Message } from '../../types';
import { renderInlineCssVars, useDesignTokens, DesignTokens } from '../../../../theme';
import { setAlpha } from '../../../../design-tokens';
import { Icon } from '../../../Icon';
import useOnFocusOut from '../../../../hooks/useOnFocusOut';
import { OptionPopper, OptionPopperOption } from '../../../OptionPopper';
import { ReactKeyboardEvent, ReactChangeEvent } from '../../../../react-synthetic-events';
import { isNullish } from '@sas/nova-core/typeguards';
import { createUseHtmlId } from '../../../../hooks/useHtmlId';
import { postfixTestId } from '../../../../util/testId';
import { InputSuggestionItem } from '../../';
import { ClearSearch, ErrorStatus, WarningStatus } from '@sas/react-svg';
import { useTruncationTooltip } from '../../../../hooks/useTruncationTooltip';
import { useDirectSpeech } from '../../../DirectSpeech/DirectSpeechProvider';
import { useGetGlobalSetting } from '../../../Settings/GlobalSettingsProvider';
import { GlobalSettingsKeys } from '../../../Settings/GlobalSettingsKeys';
import { createUseTextLocalizer } from '../../../../locale/createUseTextLocalizer';
import { useIsRTL } from '../../../../locale';
import bundle from '../../NovaComponents-Input-gui-icu.properties';
import { setRef } from '../../../../util/ref-util';
import { Text } from '../../../Text';
import { markEvent } from '../../../../util/events';
import { MessagePopover } from '../MessagePopover';
import { escapeRegex } from '../../../../util';
import { Button, ButtonVariant } from '../../../Button';
import { useMounted } from '../../../../hooks/useMounted';
import { useEarcon, EarconName } from '../../../../hooks/useEarcon';

import cx from 'classnames';
import cn from '../../Input.css.json';
import '../../Input.css';

const useListId = createUseHtmlId('Input-SuggestionList');
const useListItemId = createUseHtmlId('Input-SuggestionListItem');
const useValidationMessageId = createUseHtmlId('Input-ValidationMessage');

const useTextLocalizer = createUseTextLocalizer(bundle);

const isCustomMessage = (message: Message): message is CustomMessage =>
  (message as CustomMessage)?.message !== undefined;

export const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>(function BaseInput(
  props,
  ref
) {
  const suggestionListRef = useRef<HTMLDivElement | null>(null);
  const clearBtnRef = useRef<HTMLButtonElement | null>(null);
  const [isInEditState, setEditState] = useState<boolean>(false);
  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null | undefined>(undefined);
  const [shouldShowSuggestions, setShouldShowSuggestions] = useState<boolean>(false);
  const [suggestionsFilterValue, setSuggestionsFilterValue] = useState('');
  const localize = useTextLocalizer();
  const isRTL = useIsRTL();
  const refInput = useRef<HTMLInputElement | null>(null);
  const shouldAnnounce = useRef(false); // whether or not to announce number of results for suggestions
  const cursorPositionRef = useRef(-1);
  const suggestions = useMemo(() => {
    if (!shouldShowSuggestions) return [];
    if (props.suggestionItemsFilterFunction) {
      return props.suggestionItemsFilterFunction?.(
        suggestionsFilterValue,
        cursorPositionRef.current !== -1 ? cursorPositionRef.current : suggestionsFilterValue.length
      );
    }
    return props.suggestionItems
      ? filterSuggestions(props.suggestionItems, suggestionsFilterValue, props.filterType)
      : [];
  }, [props, shouldShowSuggestions, suggestionsFilterValue]);
  const showSuggestions = suggestions.length > 0;

  const selectSuggestionHandler = (selectedIndex: number) => {
    if (!suggestions || selectedIndex >= suggestions.length || selectedIndex < 0) {
      return;
    }
    const { text: value1 } = suggestions[selectedIndex] as OptionPopperOption;
    refInput?.current?.setSelectionRange(
      cursorPositionRef.current,
      cursorPositionRef.current + value1?.length! - 1,
      undefined
    );
    const { text: value } = suggestions[selectedIndex] as OptionPopperOption;
    props.onChange?.({
      value: value || '',
      reason: 'suggestion',
      cursorPosition: refInput?.current?.selectionStart! ?? suggestionsFilterValue.length,
      focusedIndex: selectedIndex,
    });
    props.onCommit?.({ value: value || '', reason: 'suggestion' });
    closeSuggestions();
  };

  const mounted = useMounted();
  const closeSuggestions = () => {
    if (mounted.current) {
      setShouldShowSuggestions(false);
      setFocusedIndex(undefined);
      shouldAnnounce.current = false; // prevent announcement of num results after closing suggestions
    }
  };

  const setTruncationTooltip = useTruncationTooltip();
  const type = props.type === 'number' ? 'tel' : props.type;

  const setRefInput = useCallback(
    (input: HTMLInputElement | null) => {
      refInput.current = input;
      setRef(ref, input);
      if (type !== 'password') {
        setTruncationTooltip(input);
      }
    },
    [ref, setTruncationTooltip, type]
  );

  useImperativeHandle(
    props.suggestionsRef,
    () => ({
      isOpen: () => showSuggestions,
    }),
    [showSuggestions]
  );

  const [shouldHighlight, setShouldHighlight] = useState(false);

  const editable = props.editable ?? true;

  // NOTE: this highlights the text in the textbox when the user changes the focus in the OptionPopper.
  //     | The main issue with this approach is we assume the text in the textbox will change before this happens.
  useLayoutEffect(
    function highlightValue() {
      if (!shouldHighlight) return;
      setShouldHighlight(false);
      // Addding the type check because - setSelectionRange only supports these types
      // for more info, visit - https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement
      const inputType: string[] = ['text', 'url', 'password', 'tel', 'search', 'week', 'month'];
      // Adding Timeout - many inconsistencies in highlighting for Safari browser
      setTimeout(() => {
        if (
          !refInput.current ||
          refInput.current !== document.activeElement || // Calling setSelectionRange in Safari would set focus on it, so only proceed if input currently has focus
          (type && !inputType.includes(type.toLowerCase()))
        )
          return;

        if (
          (!props.disabled && props.value && editable && !props.suggestionItemsFilterFunction) ||
          isInEditState ||
          props.variant === 'composite'
        ) {
          refInput.current.setSelectionRange(0, props.value.length, undefined);
        } else if (
          (!props.disabled && props.value && editable && props.suggestionItemsFilterFunction) ||
          isInEditState
        ) {
          if (focusedIndex !== undefined && focusedIndex !== null && focusedIndex >= 0) {
            const { text: value } = suggestions[focusedIndex!] as OptionPopperOption;
            refInput?.current?.setSelectionRange(
              cursorPositionRef.current,
              cursorPositionRef.current + value?.length! - 1,
              undefined
            );
          }
        } else {
          refInput.current.setSelectionRange(0, 0, undefined);
        }
      }, 0);
    },
    [
      shouldHighlight,
      props.value,
      type,
      props.disabled,
      isInEditState,
      editable,
      props.suggestionItemsFilterFunction,
      suggestions,
      focusedIndex,
      props.variant,
    ]
  );

  const refOnChange = useRef(props.onChange);
  useEffect(() => {
    refOnChange.current = props.onChange;
  }, [props.onChange]);

  // Emit an onChange event whenever a new suggestion value is focused
  const currentSuggestion =
    typeof focusedIndex === 'number'
      ? (suggestions?.[focusedIndex] as OptionPopperOption)?.text
      : undefined;
  useEffect(() => {
    if (isNullish(currentSuggestion)) {
      return;
    }
    // user focused a new suggestion
    refOnChange.current?.({
      value: currentSuggestion,
      reason: 'suggestion',
      cursorPosition:
        cursorPositionRef.current !== -1
          ? cursorPositionRef.current
          : suggestionsFilterValue.length,
      focusedIndex,
    });
    // NOTE: it's probably best to queue this highlight effect after we fire an event.
    setShouldHighlight(true);
  }, [currentSuggestion, suggestionsFilterValue, focusedIndex]);

  const handlers = useOnFocusOut(() => {
    closeSuggestions();
    mounted.current && setHasFocus(false);
    // TODO: don't need to fire if list item was clicked, we currently fire twice in that case.
    if (focusedIndex !== undefined && focusedIndex !== null && focusedIndex >= 0)
      props.onCommit?.({ value: props.value, reason: 'suggestion' });
    else props.onCommit?.({ value: props.value, reason: 'blur' });
  });
  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    const relatedToSuggestionList =
      suggestionListRef.current?.contains(event.relatedTarget as Node) ||
      suggestionListRef.current?.contains(event.target as Node);
    if (mounted.current && !relatedToSuggestionList) {
      handlers.onBlur();
      props.onBlur?.();
      // Added for NOVACOMMONS-966, text remains selcted when focus out via tab navigation
      !editable && window.getSelection && window.getSelection()!.empty();
      setHasOver(false);
      setHasFocus(false);
      if (props.isInPlaceEditMode) {
        setEditState(!!props.status); // IPE
      }
      shouldAnnounce.current = false;
    }
  };

  const handleFocusOnInput = () => {
    if (mounted.current) {
      handlers.onFocus();
      setHasFocus(true);
      setShouldHighlight(true);
      if (props.isInPlaceEditMode && !props.disabled) {
        setEditState(true); // IPE
      }
      props.onFocus?.();
    }
  };

  const onFocus = (e: FocusEvent) => {
    if (clearBtnRef && e.target === clearBtnRef.current) return;
    if (refInput.current && e.target !== refInput.current) {
      refInput.current.focus();
      return;
    }
    handleFocusOnInput();
  };

  const onMouseDown = (e: MouseEvent) => {
    props.onMouseDown?.(e);
  };
  const onMouseUp = (e: MouseEvent) => {
    props.onMouseUp?.(e);
    cursorPositionRef.current =
      refInput.current?.selectionStart ?? refInput.current?.value.length ?? 0;
  };

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    const isCtrlOrMetaKey = e.ctrlKey || e.metaKey;
    if (
      refInput.current &&
      editable &&
      !isCtrlOrMetaKey &&
      // ignore the unprintable characters, and the IME consideration
      (e.key.length === 1 || e.keyCode === 229)
    ) {
      if (props.maxLength && props.value.length >= props.maxLength) earcon.play?.();
    }
    const commitOnEnterInDialog = props.commitOnEnterInDialog ?? false;

    // commitOnEnterInDialog (false by default), keeps the markEvent from happening thus triggering the default action.
    // Marking this event is needed when Input is in a Dialog so that Enter doesn't trigger the Dialog's default button
    if (commitOnEnterInDialog && e.key === 'Enter') {
      markEvent(e);
    }

    if (e.key === 'Escape') {
      closeSuggestions();
      const cancelHandled = props.onCancel?.();
      setShouldHighlight(true);
      if (showSuggestions || (props.onCancel && cancelHandled !== false)) {
        if (props.role !== 'combobox') {
          e.preventDefault();
          e.stopPropagation();
        }
      }
      return;
    }
    cursorPositionRef.current =
      refInput.current?.selectionStart ?? refInput.current?.value.length ?? 0;
    if (!showSuggestions || !suggestions || suggestions.length === 0) {
      shouldAnnounce.current = false;
      return;
    }
    if (e.key === 'ArrowDown') {
      // prevent text input cursor from moving
      e.preventDefault();
      mounted.current &&
        setFocusedIndex((prevFocusedIndex) =>
          !isNullish(prevFocusedIndex) ? Math.min(prevFocusedIndex + 1, suggestions.length - 1) : 0
        );
      if (focusedIndex && focusedIndex + 1 === suggestions.length) earcon.play?.();
    } else if (e.key === 'ArrowUp') {
      // prevent text input cursor from moving
      e.preventDefault();
      mounted.current &&
        setFocusedIndex((prevFocusedIndex) =>
          !isNullish(prevFocusedIndex) ? Math.max(prevFocusedIndex - 1, 0) : suggestions.length - 1
        );
      if (focusedIndex === 0) earcon.play?.();
    }
    if (e.key === 'PageDown') {
      // prevent text input cursor from moving
      e.preventDefault();
      e.stopPropagation();
      return;
    } else if (e.key === 'PageUp') {
      // prevent text input cursor from moving
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  };

  const processChange = (value: string) => {
    props.onChange?.({ value, reason: 'onChange' });
    if (mounted.current) {
      setSuggestionsFilterValue(value);
      setShouldShowSuggestions(true);
      setFocusedIndex(undefined);
      shouldAnnounce.current = true;
    }
  };

  const handleChange = (e: ReactChangeEvent<HTMLInputElement>) => {
    cursorPositionRef.current =
      refInput.current?.selectionStart ?? refInput.current?.value.length ?? 0;
    processChange(e.target.value);
  };

  const handleClear = async () => {
    processChange('');
    refInput.current?.focus();
    handleFocusOnInput();
    refInput.current?.removeAttribute('title');
  };

  const handleKeyPress = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (mounted.current && event.key === 'Enter') {
      // user is committing the current value
      // TODO: do we need to reset the selected index too?
      closeSuggestions();
      if (focusedIndex !== undefined && focusedIndex !== null && focusedIndex >= 0) {
        props.onCommit?.({ value: props.value, reason: 'suggestion' });
      } else {
        props.onCommit?.({ value: props.value, reason: 'enter' });
      }
      if (props.isInPlaceEditMode) {
        setEditState(!!props.status); // IPE
      }
    }
  };

  const [hasHover, setHasOver] = useState<boolean>(false);

  const handleMouseOver = () => {
    setHasOver(true);
  };

  const handleMouseLeave = () => setHasOver(false);

  const dt = useDesignTokens();
  const styleTokenOverrideValues = useMemo(() => {
    const overrides = props.theme;
    const placeholder = props.placeholderTheme;
    let overrideValues = {};
    if (overrides) {
      overrideValues = {
        ...renderInlineCssVars(overrides({ state: 'normal' }), 'input'),
        ...renderInlineCssVars(overrides({ state: 'alert' }), 'input-alert'),
        ...renderInlineCssVars(overrides({ state: 'warning' }), 'input-warning'),
      };
    }
    if (placeholder) {
      overrideValues = {
        ...overrideValues,
        ...renderInlineCssVars(placeholder(), 'input-placeholder'),
      };
    }
    return overrideValues;
  }, [props.theme, props.placeholderTheme]);

  const isCustomFocusTurnedOn = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_OVERRIDE) === 'true';

  // Outline Focus Indicator
  let outline;
  let border;
  if (
    dt.themeVariant !== 'high-contrast' &&
    editable &&
    !props.disabled &&
    !isCustomFocusTurnedOn
  ) {
    outline = 'none';
  }
  if ((dt.themeVariant === 'high-contrast' || props.disabled) && props.variant === 'composite') {
    border = 'transparent';
  }

  const style = {
    ...renderInlineCssVars(getInputThemeTokens(dt, props.status), 'input'),
    ...renderInlineCssVars(
      {
        'min-width': dt.spacing.size.xxs,
        'pd-squish-s': dt.spacing.insetSquish.s.padding,
        'spacing-xs': dt.spacing.core.xs,
        'font-size-m': dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m,
        'line-height': dt.enableNovaTokens ? dt.fontNova.lineHeight : dt.font.lineHeight,
      },
      'input'
    ),
    ...renderInlineCssVars({ outline, 'noneditable-bd': border }, 'input-focus'),
    ...styleTokenOverrideValues,
    ...props.font,
  };

  const idList = useListId();
  const listItemIds = useListItemId(suggestions?.length || 0);
  const idValidationMessage = useValidationMessageId();

  const ariaDescribedBy = [];
  if (props.ariaDescribedBy) ariaDescribedBy.push(...props.ariaDescribedBy);

  // added for dir attr for these types (NOVACOMMONS-1617)
  const inputType = ['url', 'email', 'tel'];

  // IPE
  const shouldSetFocus = refInput && refInput.current && props.isInPlaceEditMode && isInEditState;
  useEffect(() => {
    if (shouldSetFocus) {
      refInput.current!.setSelectionRange(0, 0);
      refInput.current!.focus();
    }
  }, [shouldSetFocus]);

  let narratorMessage = '';
  if (suggestions?.length === 0) {
    narratorMessage = localize('narrator.noresults.msg');
  } else if (showSuggestions) {
    if (suggestions?.length === 1) narratorMessage = localize('narrator.result.msg');
    else {
      narratorMessage = localize('narrator.results.msg', {
        numResults: suggestions?.length.toString(),
      });
    }
  }

  let invalidInputMessage = '';
  if (props.status) {
    if (hasFocus && props.message) {
      invalidInputMessage = localize('invalidInput.msg', {
        message: isCustomMessage(props.message) ? props.message.message : props.message,
      });
    } else if (props.ariaLabel) {
      if (props.status === 'warning') {
        invalidInputMessage = localize('invalidEntryWarning.aria', {
          label: props.ariaLabel,
        });
      } else {
        invalidInputMessage = localize('invalidEntryError.aria', {
          label: props.ariaLabel!,
        });
      }
    }
  }

  const directSpeech = useDirectSpeech();
  useEffect(() => {
    if (props.status) {
      directSpeech?.notify(invalidInputMessage);
    }
    if (
      hasFocus &&
      shouldAnnounce.current &&
      props.value !== '' &&
      props.suggestionItems &&
      suggestions?.length >= 0
    ) {
      directSpeech?.notify(narratorMessage);
      if (showSuggestions) shouldAnnounce.current = false; // don't announce num results while scrolling suggestions list
    }
  }, [
    props.status,
    directSpeech,
    invalidInputMessage,
    showSuggestions,
    props.suggestionItems,
    suggestions?.length,
    narratorMessage,
    hasFocus,
    props.value,
    shouldAnnounce,
  ]);

  const showClearButton =
    !!props.showClearButton &&
    props.value !== '' &&
    props.required !== true &&
    props.disabled !== true &&
    (editable || (!editable && props.variant === 'composite'));
  const earcon = useEarcon({ source: EarconName.InputFull });

  return (
    <div
      className={cx(
        cn.container,
        {
          [cn.hasStatusXorClear]: !!props.status !== showClearButton,
          [cn.hasStatusAndClear]: !!props.status && showClearButton,
        },
        props.className
      )}
      style={{ ...style, ...props.style }}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseDown={(event: MouseEvent) => {
        onMouseDown(event);
      }}
      onMouseUp={(event: MouseEvent) => {
        onMouseUp(event);
      }}
      data-testid={props.testId}
    >
      {props.isInPlaceEditMode && !isInEditState ? ( // IPE
        <Text
          className={cn.inplaceEdit}
          testId={postfixTestId(props.testId, 'inplaceEdit')}
          disabled={props.disabled}
          focusable
          wrapText={false}
        >
          {props.value}
        </Text>
      ) : (
        <input
          dir={isRTL && type && inputType.includes(type.toLowerCase()) ? 'ltr' : undefined}
          style={props.font}
          tabIndex={props.tabindex}
          className={cx(cn.input, {
            [cn.hasInputIcon]: props.status,
            [cn.hideBorder]: props.hideBorder,
            [cn.disabled]: props.disabled,
            [cn.readonly]: !editable,
            [cn.composite]: props.variant === 'composite',
            [cn.rtl]: isRTL,
          })}
          data-testid={postfixTestId(props.testId, 'input')}
          ref={setRefInput}
          value={props.value}
          placeholder={
            !editable && (props.variant === undefined || props.variant === 'standard')
              ? undefined
              : props.placeholder!
          }
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onKeyPress={handleKeyPress}
          onMouseEnter={handleMouseOver}
          onMouseLeave={handleMouseLeave}
          onKeyUp={() => {}}
          type={type === 'email' ? 'text' : type ?? 'text'}
          autoComplete={
            type === 'password' ? 'new-password' : !!props.enableAutoComplete ? 'on' : 'off'
          }
          // Set aria-autocomplete='list' here if there are suggestions displayed (for now).  This setting should be changed to be aria-autocomplete='both'
          // in the future when the highlighted "type-ahead" functionality is added to the Input (when only one filtered selection exists). This directive
          // is per the recent Accessibility Clinic review of Input (8/10/2021) with Jesse Sookne/Greg Kraus.
          aria-autocomplete={showSuggestions ? 'list' : undefined}
          aria-haspopup={showSuggestions ? true : undefined}
          aria-required={props.required}
          aria-disabled={props.disabled}
          aria-invalid={props.status === 'alert' || props.ariaInvalid ? true : undefined}
          aria-describedby={ariaDescribedBy.length > 0 ? ariaDescribedBy.join(' ') : undefined}
          aria-controls={showSuggestions ? idList : undefined}
          aria-activedescendant={
            showSuggestions && !isNullish(focusedIndex) ? listItemIds[focusedIndex] : undefined
          }
          aria-label={props.ariaLabel}
          aria-labelledby={props.ariaLabelledBy}
          id={props.id}
          readOnly={!editable || props.disabled}
          aria-readonly={!editable}
          maxLength={props.maxLength}
          data-landmark-label={props.landmarkLabel}
          role={props.role}
        />
      )}
      {props.status ? (
        <span aria-live="off" role="status" style={{ width: '0', overflow: 'hidden' }}>
          <span className={cn.offscreen} id={idValidationMessage}>
            {localize('invalidInput.msg', {
              message: isCustomMessage(props.message) ? props.message.message : props.message!,
            })}
          </span>
        </span>
      ) : null}
      {showClearButton ? (
        <Button
          ref={clearBtnRef}
          className={cn.clearButton}
          variant={ButtonVariant.Icon}
          disabled={props.disabled}
          icon={{
            icon: ClearSearch,
            tooltip: props.clearButtonTooltip ?? localize('clear.tooltip.label'),
            ariaLabel: props.clearButtonAriaLabel,
          }}
          onPress={() => {
            handleClear();
          }}
          testId={postfixTestId(props.testId, 'clearButton')}
          tokens={props.clearButtonTokens}
        />
      ) : null}
      {!hasFocus && props.status ? (
        <div
          className={cn.inputIcon}
          onMouseEnter={handleMouseOver}
          onMouseLeave={handleMouseLeave}
        >
          <Icon
            icon={props.status === 'alert' ? ErrorStatus : WarningStatus}
            color={props.status}
            testId={postfixTestId(props.testId, 'statusIcon')}
          />
        </div>
      ) : null}
      <OptionPopper
        ref={suggestionListRef}
        onClose={() => {}}
        focusedIndex={focusedIndex}
        listId={idList}
        style={{
          width: props.style?.width ?? refInput?.current?.width,
          overflow: 'auto',
        }}
        isOpen={showSuggestions}
        referenceElementRef={refInput}
        {...(props.enableVirtualScrolling
          ? {
              virtualItems: {
                count: suggestions.length ?? 0,
                renderItem: ({ index }) => {
                  return {
                    text: (suggestions[index] as OptionPopperOption).text,
                    secondaryText: (suggestions[index] as OptionPopperOption).secondaryText,
                    concatSecondaryText: (suggestions[index] as OptionPopperOption)
                      .concatSecondaryText,
                    icon: (suggestions[index] as OptionPopperOption).icon,
                    iconAriaLabel: (suggestions[index] as OptionPopperOption).iconAriaLabel,
                    iconTooltip: (suggestions[index] as OptionPopperOption).iconTooltip,
                    id: (suggestions[index] as OptionPopperOption).id,
                    indented: (suggestions[index] as OptionPopperOption).indented,
                    tooltip: (suggestions[index] as OptionPopperOption).tooltip,
                    showSeparator: (suggestions[index] as OptionPopperOption).showSeparator,
                  };
                },
                width: props.inputContainerWidth ?? refInput.current?.getBoundingClientRect().width,
              },
            }
          : { items: suggestions?.map((s, i) => ({ ...s, id: listItemIds[i] })) })}
        onSelectFocusedItem={() =>
          !isNullish(focusedIndex) && selectSuggestionHandler(focusedIndex)
        }
        onSelected={({ index }) => selectSuggestionHandler(index)}
        listItemFocusable={false}
        testId={postfixTestId(props.testId, 'suggestionPopper')}
        ariaLabel={props.ariaLabelForSuggestionsList ?? localize('suggestedValues.aria')}
        focusLockProps={{ disabled: true }}
      />
      {props.status ? (
        <MessagePopover
          status={props.status}
          referenceElementRef={refInput}
          {...(isCustomMessage(props.message) ? props.message : { message: props.message! })}
          hidden={!hasFocus && !hasHover}
          testId={postfixTestId(props.testId, 'messagePopper')}
        />
      ) : null}
    </div>
  );
});

/**
 * Look at `__tests__/InputSuggestionRegex.spec.ts` for usage.
 */
export function createInputSuggestionRegex(
  value: string,
  filterType: BaseInputProps['filterType'] = 'beginsWith'
): RegExp | null {
  if (value === '') {
    return null;
  }

  const pattern = escapeRegex(value).replace(/\s/g, '\\s');

  const filteredPattern =
    filterType === 'contains' ? new RegExp(`${pattern}`, 'i') : new RegExp(`^${pattern}`, 'i');
  return filteredPattern;
}

function filterSuggestions(
  suggestionItems: readonly InputSuggestionItem[],
  value: string,
  filterType?: BaseInputProps['filterType']
): OptionPopperOption[] {
  const pattern = createInputSuggestionRegex(value, filterType);
  // we could not create a valid pattern
  if (!pattern) return [];
  return suggestionItems.filter(
    (item) =>
      (item.text && pattern.test(item.text)) ||
      (item.secondaryText && pattern.test(item.secondaryText))
  );
}

export const getInputThemeTokens = (dt: DesignTokens, status: BaseInputProps['status']) => {
  if (dt.enableNovaTokens) {
    if (status) {
      return {
        ...dt.stateStatusNova[status],
        'placeholder-fg': dt.c.colorTextLabel,
        'selection-bg': dt.textNova.selection.bg,
        'selection-fg': dt.textNova.selection.fg,
        'disabled-opacity': 1,
        'disabled-fg': dt.stateInputNova.disabled.fg,
        'disabled-bd': dt.stateInputNova.disabled.bd,
        'disabled-bg': dt.stateInputNova.disabled.bg,
        'noneditable-bg': dt.stateInputNova.nonEditable.bg,
        'noneditable-bd': dt.stateInputNova.nonEditable.bd,
        fg: dt.c.colorTextDefault,
      };
    } else {
      return {
        ...dt.stateInputNova.normal,
        'selection-bg': dt.textNova.selection.bg,
        'selection-fg': dt.textNova.selection.fg,
        'disabled-opacity': 1,
        'focus-bd': dt.stateInputNova.focused.bd,
        'placeholder-fg': dt.c.colorTextLabel,
        'readonly-bg': dt.stateInputNova.readOnly.bg,
        'readonly-bd': dt.stateInputNova.readOnly.bd,
        'readonly-fg': dt.stateInputNova.readOnly.fg,
        'noneditable-bg': dt.stateInputNova.nonEditable.bg,
        'noneditable-bd': dt.stateInputNova.nonEditable.bd,
        'noneditable-fg': dt.stateInputNova.nonEditable.fg,
        'disabled-fg': dt.stateInputNova.disabled.fg,
        'disabled-bd': dt.stateInputNova.disabled.bd,
        'disabled-bg': dt.stateInputNova.disabled.bg,
      };
    }
  } else {
    // legacy tokens
    if (status) {
      const statusTokens = {
        ...dt.stateStatus[status],
        'selection-bg': dt.text.selection.bg,
        'selection-fg': dt.text.selection.fg,
      };

      if (
        dt.baseReportThemeName === 'highcontrast2017' ||
        dt.baseReportThemeName === 'highcontrast2018'
      ) {
        return {
          ...statusTokens,
          'placeholder-fg': dt.c.hcDisabled,
          'disabled-opacity': 0.5,
        };
      }

      switch (dt.themeVariant) {
        case 'dark':
          return {
            ...statusTokens,
            'placeholder-fg': setAlpha(dt.c.white, 0.3),
            'disabled-opacity': 0.25,
          };
        case 'high-contrast':
          return {
            ...statusTokens,
            'placeholder-fg': setAlpha(dt.c.white, 0.5),
            'disabled-opacity': 0.5,
          };
        default:
          return {
            ...statusTokens,
            'placeholder-fg': dt.c.n1,
            'disabled-opacity': 0.3,
          };
      }
    } else {
      if (
        dt.baseReportThemeName === 'highcontrast2017' ||
        dt.baseReportThemeName === 'highcontrast2018'
      ) {
        return {
          bd: dt.c.white,
          bg: dt.c.black,
          fg: dt.c.white,

          'selection-bg': dt.text.selection.bg,
          'selection-fg': dt.text.selection.fg,

          'placeholder-fg': dt.c.hcDisabled,
          'disabled-opacity': 0.5,
          'focus-bd': dt.c.p0,
        };
      }

      switch (dt.themeVariant) {
        case 'dark':
          return {
            bd: setAlpha(dt.c.white, 0.3),
            bg: dt.c.p_3,
            fg: dt.c.white,

            'selection-bg': dt.text.selection.bg,
            'selection-fg': dt.text.selection.fg,

            'placeholder-fg': setAlpha(dt.c.white, 0.3),
            'disabled-opacity': 0.25,
            'focus-bd': dt.c.p0,
          };
        case 'high-contrast':
          return {
            bd: dt.c.white,
            bg: dt.c.black,
            fg: dt.c.white,

            'selection-bg': dt.text.selection.bg,
            'selection-fg': dt.text.selection.fg,

            'placeholder-fg': setAlpha(dt.c.white, 0.5),
            'disabled-opacity': 0.5,
            'focus-bd': dt.c.p3,
          };
        default:
          return {
            bd: dt.c.n1,
            bg: dt.c.white,
            fg: dt.c.n_3,

            'selection-bg': dt.text.selection.bg,
            'selection-fg': dt.text.selection.fg,

            'placeholder-fg': dt.c.n1,
            'disabled-opacity': 0.3,
            'focus-bd': dt.c.p0,
          };
      }
    }
  }
};
